import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { startWith, switchMap, tap } from 'rxjs/operators';
import { LogoutAction } from '../authentication/auth.state.model';

const TIMEOUT_MS = 60 * 60 * 1000;
// const TIMEOUT_MS = 60 * 1000; // 1 Minute für Testzwecke
const ACTIVITY_EVENTS = ['mousemove', 'click', 'keydown', 'scroll', 'touchstart'];

@Injectable({
  providedIn: 'root'
})
export class InactivityService implements OnDestroy {
  private document = inject(DOCUMENT);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private subscription: Subscription | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private lastActivity = 0;

  public readonly wasAutoLoggedOut = signal(false);
  public readonly remainingSeconds = signal<number>(TIMEOUT_MS / 1000);

  start(): void {
    if (this.subscription) {
      return;
    }

    this.lastActivity = Date.now();
    this.remainingSeconds.set(TIMEOUT_MS / 1000);

    const activity$ = merge(
      ...ACTIVITY_EVENTS.map(event => fromEvent(this.document, event))
    );

    this.subscription = activity$.pipe(
      startWith(null),
      tap(() => {
        this.lastActivity = Date.now();
        this.remainingSeconds.set(TIMEOUT_MS / 1000);
      }),
      switchMap(() => timer(TIMEOUT_MS))
    ).subscribe(() => {
      this.dialog.closeAll();
      this.wasAutoLoggedOut.set(true);
      this.store.dispatch(new LogoutAction());
    });

    this.countdownInterval = setInterval(() => {
      const elapsed = Date.now() - this.lastActivity;
      const remaining = Math.max(0, Math.ceil((TIMEOUT_MS - elapsed) / 1000));
      this.remainingSeconds.set(remaining);
    }, 1000);
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
